/* The card's "Additional fields": the company's own fields, set up in the admin, on the Overview —
   each a fact edited in place (`InlineField`), the editor by type below.

   `custom_fields` on `GET excalibur/counterparties/:id/` is every field (deleted ones are not sent), in order:
   `{id, code, label, type, options, value}`. `type` is text | number | boolean | date | choice |
   multiselect; `options` (choice and multiselect) are `{id, label}`. Options are soft-deleted in
   the admin: a deleted one comes nowhere — not in `options`, not in `value` (a field holding one
   arrives as null, empty) — and picking it is a 400. `value` is a string, a JSON number (6.0
   arrives as 6), a boolean, "YYYY-MM-DD", `{id, label}`, `[{id, label}]` (multiselect), or null.
   A multiselect is written as `value: [<option_id>, …]`; null or [] clears it.

   A card PATCH takes `custom_fields: [{field_id, value}]` (JSON only) — partial: only the fields
   sent change, null clears (empty text too). Errors come back per ITEM, in the order sent:
   `{custom_fields: [{}, {value: [...]}]}` — `Page.js` maps the index back to the field.

   The editors follow what the API takes: text ≤ 1000 characters (`CUSTOM_TEXT_MAX_LENGTH`), a
   growing TextArea since a note holds line breaks; a
   number of at most 15 digits, 4 after the separator (no `precision`, so nothing is rounded on
   the way), negative allowed; a yes / no Select with a clear button, because a Switch has no third state for "not
   filled in"; the card's own date picker; a choice Select over `options` as they come, and the
   same Select with `mode="multiple"` for a multiselect.

   The number box takes digits, one "-" in front and ONE separator — the interface language's —
   from the keyboard, and a paste only when the text it leaves is a number. Its `parser` keeps
   every other character: antd's own strips all but [A-Za-z0-9_.-], so Cyrillic text became "",
   which `InputNumber` reads as a cleared box — null, and the saved value was gone. Now text that is
   not a number never changes the value (and is put back on blur); null comes only from a box the
   user emptied. */

import React from "react";
import { DatePicker, Input, InputNumber, Select, Tag } from "antd";

import { COUNTERPARTIES_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

const CF_TEXT_MAX = 1000;
const NUMBER_MAX = 99999999999.9999;
/* A whole number, as the list's filter checks it (`customFieldFilters.js`); and one still being typed. */
const NUMBER_RE = /^-?\d{1,11}(\.\d{1,4})?$/;
const PARTIAL_RE = /^-?\d*\.?\d*$/;

/** The box's text as a number string: no spaces, the separator as ".". */
const normalizeNumber = (text, separator) =>
  String(text)
    .replace(/\s/g, "")
    .replace(separator, ".");
/** The input's text once `insert` replaces its selection. */
const textWith = (input, insert) =>
  input.value.slice(0, input.selectionStart ?? input.value.length) + insert + input.value.slice(input.selectionEnd ?? input.value.length);

export const cfFieldId = field => `cp-field-cf-${field.id}`;

/** The value in the shape the API takes and the dirty check compares; "nothing" is always null. */
export function cfWire(field) {
  const value = field.value;
  if (value === null || value === undefined) return null;
  if (field.type === "choice") return value.id ?? null;
  /* A set: sorted, so the order picked is not a change; none is null, as for every other type. */
  if (field.type === "multiselect") return value.length ? value.map(o => o.id).sort() : null;
  if (field.type === "text") return String(value).trim() ? String(value) : null;
  return value;
}

/** The decimal separator of the interface language, from Intl (`1.5` written out). */
export const decimalSeparator = lang =>
  (1.5)
    .toLocaleString(localeFor(lang))
    .replace(/\d/g, "")
    .charAt(0) || ".";

/** A value as shown at rest; `null` for "nothing" — `InlineField` draws the one empty state. A text
    keeps its line breaks (the value box is `pre-line`). */
export function cfDisplay(field, lang) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const value = field.value;
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length)) return null;
  switch (field.type) {
    case "number":
      return <span className="lp-num">{new Intl.NumberFormat(localeFor(lang), { maximumFractionDigits: 4 }).format(Number(value))}</span>;
    case "boolean":
      return t(value ? "cf_yes" : "cf_no");
    case "date":
      return dayjs(value).format("DD.MM.YYYY");
    /* Picked options are neutral tags (`.cp-tag`, tokens only), wrapping inside the value. */
    case "choice":
      return <Tag className="cp-tag">{value.label}</Tag>;
    case "multiselect":
      return (
        <span className="cp-tags">
          {value.map(o => (
            <Tag className="cp-tag" key={o.id}>
              {o.label}
            </Tag>
          ))}
        </span>
      );
    default:
      return String(value);
  }
}

/** The editor of one field, by its type. `onChange` gets the new `value` in the card's own shape. */
export function cfEditor(field, onChange, { lang, status }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const id = cfFieldId(field);
  const value = field.value;
  switch (field.type) {
    case "number": {
      const separator = decimalSeparator(lang);
      return (
        <InputNumber
          id={id}
          status={status}
          style={{ width: "100%" }}
          value={value ?? null}
          max={NUMBER_MAX}
          min={-NUMBER_MAX}
          decimalSeparator={separator}
          parser={text => normalizeNumber(text, separator)}
          onKeyDown={event => {
            /* A printable key (no Ctrl / ⌘ shortcut) goes in only if it keeps the text a number in the making. */
            if (event.key.length !== 1 || event.ctrlKey || event.metaKey) return;
            const next = textWith(event.currentTarget, event.key);
            if (/\s/.test(event.key) || (event.key === "." && separator !== ".") || !PARTIAL_RE.test(normalizeNumber(next, separator))) event.preventDefault();
          }}
          onPaste={event => {
            if (!NUMBER_RE.test(normalizeNumber(textWith(event.currentTarget, event.clipboardData.getData("text")), separator))) event.preventDefault();
          }}
          onChange={next => onChange(next === null || next === undefined || next === "" ? null : Number(next))}
        />
      );
    }
    case "boolean":
      return (
        <Select
          id={id}
          status={status}
          allowClear
          placeholder={t("value_empty")}
          value={value === null || value === undefined ? undefined : value}
          options={[
            { value: true, label: t("cf_yes") },
            { value: false, label: t("cf_no") },
          ]}
          onChange={next => onChange(next === undefined ? null : next)}
        />
      );
    case "date":
      return (
        <DatePicker
          id={id}
          status={status}
          style={{ width: "100%" }}
          format="DD.MM.YYYY"
          placeholder={t("value_empty")}
          value={value ? dayjs(value) : null}
          onChange={next => onChange(next ? next.format("YYYY-MM-DD") : null)}
        />
      );
    case "choice":
      return (
        <Select
          id={id}
          status={status}
          allowClear
          placeholder={t("value_empty")}
          value={value?.id ?? undefined}
          options={(field.options || []).map(o => ({ value: o.id, label: o.label }))}
          onChange={next => onChange(next === undefined ? null : (field.options || []).find(o => o.id === next) || null)}
        />
      );
    case "multiselect":
      return (
        <Select
          id={id}
          status={status}
          mode="multiple"
          allowClear
          /* No `maxTagCount="responsive"`: rc-overflow measures while the editor's popover is still
             scaling in and settles on "+ 0 …". The chips wrap instead — the editor floats over the
             card, so a taller control moves nothing. */
          optionFilterProp="label"
          placeholder={t("value_empty")}
          value={(value || []).map(o => o.id)}
          options={(field.options || []).map(o => ({ value: o.id, label: o.label }))}
          onChange={ids => onChange(ids.length ? (field.options || []).filter(o => ids.includes(o.id)) : null)}
        />
      );
    default:
      /* A text may hold line breaks (a note), so it is a growing TextArea; Ctrl/⌘+Enter saves it. */
      return (
        <Input.TextArea
          id={id}
          status={status}
          autoSize={{ minRows: 2, maxRows: 8 }}
          maxLength={CF_TEXT_MAX}
          placeholder={t("value_empty")}
          value={value ?? ""}
          onChange={e => onChange(e.target.value)}
        />
      );
  }
}

/** The hint under an editor, where its type needs one. */
export function cfHint(field, lang) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  if (field.type === "boolean") return t("cf_clear_hint");
  return undefined;
}
